import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
    RiEyeLine,
    RiArrowRightLine,
    RiPencilLine,
    RiBankCard2Line,
    RiBox3Line,
    RiSearchLine,
    RiArrowLeftSLine,
    RiArrowRightSLine,
} from 'react-icons/ri'
import { useAdminOrders, useUpdateOrderStatus } from '@/api/useAdminOrders'
import type { OrderItemDetail } from '@/api/useAdminOrders'
import { OrderStatusBadge } from '@/components/orders/OrderStatusBadge/OrderStatusBadge'
import { StatusChangeModal } from '@/components/admin/StatusChangeModal'
import { Spinner } from '@/components/ui'
import { useModalStore } from '@/stores/useModalStore'
import { buildRoute } from '@/constants/routes'
import styles from './AdminOrders.module.css'

const STATUS_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'confirmed', label: 'Confirmado' },
    { value: 'printing', label: 'En impresión' },
    { value: 'shipped', label: 'Enviado' },
    { value: 'delivered', label: 'Entregado' },
    { value: 'cancelled', label: 'Cancelado' },
]

const PAYMENT_STATUS_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'unpaid', label: 'Sin pagar' },
    { value: 'paid', label: 'Pagado' },
    { value: 'refunded', label: 'Reembolsado' },
]

const VALID_TRANSITIONS: Record<string, { value: string; label: string; color: string }[]> = {
    pending: [
        { value: 'confirmed', label: 'Confirmar', color: 'info' },
        { value: 'cancelled', label: 'Cancelar', color: 'danger' },
    ],
    confirmed: [
        { value: 'printing', label: 'Enviar a impresión', color: 'primary' },
        { value: 'cancelled', label: 'Cancelar', color: 'danger' },
    ],
    printing: [
        { value: 'shipped', label: 'Marcar enviado', color: 'success' },
    ],
    shipped: [
        { value: 'delivered', label: 'Marcar entregado', color: 'success' },
    ],
    delivered: [],
    cancelled: [],
}

const PAGE_SIZE = 10

function AdminOrderItem({ item, index, onViewDesign }: {
    item: OrderItemDetail
    index: number
    onViewDesign: (projectId: string) => void
}) {
    const qty = item.quantity_millares === 1 ? '1 millar' : `${item.quantity_millares} millares`
    const frontLabel = item.front_page_number != null ? `Pág. ${item.front_page_number}` : '—'
    const backLabel = item.back_page_number != null ? `Pág. ${item.back_page_number}` : 'Sin reverso'
    return (
        <div className={styles.itemCard}>
            <div className={styles.itemIndex}>{index + 1}</div>
            <div className={styles.itemContent}>
                <div className={styles.itemHeader}>
                    <span className={styles.itemName}>{item.project_name}</span>
                    <span className={styles.itemSubtotal}>S/ {Number(item.subtotal).toFixed(2)}</span>
                </div>
                <p className={styles.itemMeta}>
                    {item.material_name} · {item.size_name} · {qty}
                </p>
                <p className={styles.itemMeta}>
                    Anverso: {frontLabel} · Reverso: {backLabel}
                </p>
                {item.project && (
                    <button
                        className={styles.designBtn}
                        onClick={() => onViewDesign(item.project as string)}
                    >
                        <RiPencilLine /> Ver diseño
                    </button>
                )}
            </div>
        </div>
    )
}

function PaginationControls({
    page,
    totalPages,
    onPageChange,
}: {
    page: number
    totalPages: number
    onPageChange: (p: number) => void
}) {
    if (totalPages <= 1) return null

    const getPages = (): (number | '...')[] => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        }
        const pages: (number | '...')[] = [1]
        if (page > 3) pages.push('...')
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
            pages.push(i)
        }
        if (page < totalPages - 2) pages.push('...')
        pages.push(totalPages)
        return pages
    }

    return (
        <div className={styles.pagination}>
            <button
                className={styles.pageBtn}
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
                aria-label="Página anterior"
            >
                <RiArrowLeftSLine />
            </button>

            {getPages().map((p, idx) =>
                p === '...' ? (
                    <span key={`ellipsis-${idx}`} className={styles.pageEllipsis}>…</span>
                ) : (
                    <button
                        key={p}
                        className={`${styles.pageBtn} ${page === p ? styles.pageBtnActive : ''}`}
                        onClick={() => onPageChange(p as number)}
                        aria-current={page === p ? 'page' : undefined}
                    >
                        {p}
                    </button>
                )
            )}

            <button
                className={styles.pageBtn}
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages}
                aria-label="Página siguiente"
            >
                <RiArrowRightSLine />
            </button>
        </div>
    )
}

export function AdminOrders() {
    const navigate = useNavigate()
    const [statusFilter, setStatusFilter] = useState('')
    const [paymentFilter, setPaymentFilter] = useState('')
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [expandedId, setExpandedId] = useState<string | null>(null)

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const { data, isLoading } = useAdminOrders({
        status: statusFilter || undefined,
        payment_status: paymentFilter || undefined,
        search: search || undefined,
        page,
    })
    const updateStatus = useUpdateOrderStatus()

    const orders = data?.orders ?? []
    const count = data?.count ?? 0
    const totalPages = data?.totalPages ?? 1

    const startItem = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
    const endItem = Math.min(page * PAGE_SIZE, count)

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            setSearch(searchInput)
            setPage(1)
        }, 500)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchInput])

    const handleStatusFilter = (value: string) => {
        setStatusFilter(value)
        setPage(1)
    }

    const handlePaymentFilter = (value: string) => {
        setPaymentFilter(value)
        setPage(1)
    }

    const openStatusModal = (orderUuid: string, newStatus: string, statusLabel: string) => {
        useModalStore.getState().openModal(
            <StatusChangeModal
                orderId={orderUuid}
                newStatus={newStatus}
                statusLabel={statusLabel}
                onConfirm={({ note, tracking_number }) => {
                    updateStatus.mutate(
                        { uuid: orderUuid, status: newStatus, note, tracking_number },
                        {
                            onSuccess: () => {
                                toast.success('Estado actualizado')
                                useModalStore.getState().closeModal()
                                setExpandedId(null)
                            },
                            onError: () => toast.error('Error al actualizar estado'),
                        }
                    )
                }}
            />
        )
    }

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Gestión de Pedidos</h1>

            {/* Search */}
            <div className={styles.searchRow}>
                <div className={styles.searchWrapper}>
                    <RiSearchLine className={styles.searchIcon} />
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Buscar por número de pedido o email…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                </div>
            </div>

            {/* Status filters */}
            <div className={styles.filterRow}>
                <div className={styles.filterScroll}>
                    {STATUS_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            className={`${styles.filterButton} ${statusFilter === opt.value ? styles.active : ''}`}
                            onClick={() => handleStatusFilter(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Payment status filters */}
            <div className={styles.filterRow}>
                <div className={styles.filterScroll}>
                    {PAYMENT_STATUS_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            className={`${styles.filterButton} ${styles.filterButtonSecondary} ${paymentFilter === opt.value ? styles.activeSecondary : ''}`}
                            onClick={() => handlePaymentFilter(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading ? (
                <div className={styles.loading}><Spinner size="lg" /></div>
            ) : orders.length === 0 ? (
                <p className={styles.empty}>No hay pedidos</p>
            ) : (
                <>
                    {count > 0 && (
                        <p className={styles.resultCount}>
                            Mostrando {startItem}–{endItem} de {count} pedidos
                        </p>
                    )}

                    <div className={styles.orderList}>
                        {orders.map((order) => {
                            const transitions = VALID_TRANSITIONS[order.status] || []
                            const isExpanded = expandedId === order.uuid
                            const itemCount = order.items?.length ?? 0

                            return (
                                <div key={order.uuid} className={styles.orderCard}>
                                    <div
                                        className={styles.orderHeader}
                                        onClick={() => setExpandedId(isExpanded ? null : order.uuid)}
                                    >
                                        <div className={styles.orderMain}>
                                            <span className={styles.orderNumber}>{order.order_number}</span>
                                            <OrderStatusBadge status={order.status} />
                                            <span className={`${styles.paymentBadge} ${styles[`payment_${order.payment_status}`]}`}>
                                                {order.payment_status === 'paid' ? 'Pagado' : order.payment_status === 'refunded' ? 'Reembolsado' : 'Sin pagar'}
                                            </span>
                                            {itemCount > 0 && (
                                                <span className={styles.itemCountBadge}>
                                                    <RiBox3Line /> {itemCount}
                                                </span>
                                            )}
                                        </div>
                                        <div className={styles.orderMeta}>
                                            <span>S/ {order.total}</span>
                                            <span className={styles.dot}>·</span>
                                            <span>{new Date(order.created).toLocaleDateString('es-PE')}</span>
                                        </div>
                                        <button className={styles.expandBtn} aria-label={isExpanded ? 'Colapsar' : 'Expandir'}>
                                            <RiEyeLine />
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className={styles.orderBody}>
                                            {/* Payment info */}
                                            {order.payment_method && (
                                                <div className={styles.paymentSection}>
                                                    <RiBankCard2Line className={styles.paymentIcon} />
                                                    <div className={styles.paymentDetails}>
                                                        <span className={styles.paymentLabel}>Pago</span>
                                                        <span className={styles.paymentMethod}>{order.payment_method}</span>
                                                        {order.izipay_transaction_id && (
                                                            <span className={styles.transactionId}>
                                                                TX: {order.izipay_transaction_id}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Items list */}
                                            {order.items && order.items.length > 0 && (
                                                <div className={styles.itemsSection}>
                                                    <span className={styles.sectionLabel}>
                                                        Productos ({order.items.length})
                                                    </span>
                                                    <div className={styles.itemsList}>
                                                        {order.items.map((item, idx) => (
                                                            <AdminOrderItem
                                                                key={item.id}
                                                                item={item}
                                                                index={idx}
                                                                onViewDesign={(projectId) => navigate(buildRoute.editor(projectId))}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Totals */}
                                            <div className={styles.totalsSection}>
                                                <span className={styles.sectionLabel}>Totales</span>
                                                <div className={styles.detailGrid}>
                                                    {order.items_total && (
                                                        <div className={styles.detailItem}>
                                                            <span className={styles.detailLabel}>Productos</span>
                                                            <span>S/ {order.items_total}</span>
                                                        </div>
                                                    )}
                                                    <div className={styles.detailItem}>
                                                        <span className={styles.detailLabel}>Envío</span>
                                                        <span>S/ {order.shipping_cost}</span>
                                                    </div>
                                                    <div className={styles.detailItem}>
                                                        <span className={styles.detailLabel}>Total</span>
                                                        <span className={styles.totalValue}>S/ {order.total}</span>
                                                    </div>
                                                    {order.tracking_number && (
                                                        <div className={styles.detailItem}>
                                                            <span className={styles.detailLabel}>Seguimiento</span>
                                                            <span>{order.tracking_number}</span>
                                                        </div>
                                                    )}
                                                    {order.notes && (
                                                        <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                                                            <span className={styles.detailLabel}>Notas del cliente</span>
                                                            <span>{order.notes}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Status logs */}
                                            {order.status_logs && order.status_logs.length > 0 && (
                                                <div className={styles.logs}>
                                                    <span className={styles.sectionLabel}>Historial</span>
                                                    {order.status_logs.map((log) => (
                                                        <div key={log.id} className={styles.logEntry}>
                                                            <OrderStatusBadge status={log.old_status} />
                                                            <RiArrowRightLine className={styles.logArrow} />
                                                            <OrderStatusBadge status={log.new_status} />
                                                            <span className={styles.logMeta}>
                                                                {log.changed_by_email} · {new Date(log.created).toLocaleString('es-PE')}
                                                            </span>
                                                            {log.note && <span className={styles.logNote}>{log.note}</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Action buttons */}
                                            {transitions.length > 0 && (
                                                <div className={styles.actionButtons}>
                                                    {transitions.map((t) => (
                                                        <button
                                                            key={t.value}
                                                            className={`${styles.actionBtn} ${styles[t.color]}`}
                                                            onClick={() => openStatusModal(order.uuid, t.value, t.label)}
                                                            disabled={updateStatus.isPending}
                                                        >
                                                            {t.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    <PaginationControls
                        page={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                    />
                </>
            )}
        </div>
    )
}
